/**
 * Component that is used in cases where a customer
 * wants to update its shipping address. In this case
 * we validate the zip code and updated shipping
 * address provided by the customer.
 *
 * @component
 */

import React from 'react';
import axios from 'axios';
import { Row, Col, Input, Card, Divider, Button, message, Form } from 'antd';
import { FormattedMessage, injectIntl } from 'react-intl';

class ExchangeShippingAddress extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      first_name: '',
      last_name: '',
      address1: '',
      address2: '',
      province: '',
      city: '',
      country: '',
      zip: '',
      submitButtonDisabled: false,
    };
  }

  async componentDidMount() {
    this.setState({
      ...this.props.orderData?.shipping_address,
    });
    // scroll to top of page
    window.scrollTo(0, 0);
  }

  async handleSubmit() {
    const { companyIdentifier, returnNumber, intl } = this.props;
    const { formatMessage } = intl;
    const {
      first_name,
      last_name,
      address1,
      address2,
      city,
      province,
      country,
      zip,
    } = this.state;

    this.setState({
      submitButtonDisabled: true,
    });

    if (
      // Comment this because case company id in shopify order
      // this.checkField(
      //   first_name,
      //   "Please enter first name. Otherwise we'll not be able to process your return."
      // ) ||
      // this.checkField(
      //   last_name,
      //   "Please enter last name. Otherwise we'll not be able to process your return."
      // ) ||
      this.checkField(
        address1,
        formatMessage({
          id: 'app.visitor.shippingAddress.addressLine1.warning',
          description: 'Error message for missing address line 1',
          defaultMessage:
            "Please enter address line 1. Otherwise we'll not be able to process your return.",
        })
      ) ||
      this.checkField(
        city,
        formatMessage({
          id: 'app.visitor.shippingAddress.city.warning',
          description: 'Error message for missing city',
          defaultMessage:
            "Please enter city. Otherwise we'll not be able to process your return.",
        })
      ) ||
      this.checkField(
        province,
        formatMessage({
          id: 'app.visitor.shippingAddress.province.warning',
          description: 'Error message for missing province',
          defaultMessage:
            "Please enter province or state. Otherwise we'll not be able to process your return.",
        })
      ) ||
      this.checkField(
        country,
        formatMessage({
          id: 'app.visitor.shippingAddress.country.warning',
          description: 'Error message for missing country',
          defaultMessage:
            "Please enter country. Otherwise we'll not be able to process your return.",
        })
      ) ||
      this.checkField(
        zip,
        formatMessage({
          id: 'app.visitor.shippingAddress.zip.warning',
          description: 'Error message for missing zip code',
          defaultMessage:
            "Please enter zip/postal code. Otherwise we'll not be able to process your return.",
        })
      )
    ) {
      return;
    }

    const countryCode =
      countryNameToCode[this.state.country?.replace(/\s+/g, '')];
    const zipPattern = zipCodeRegexByCountry[countryCode?.toUpperCase()];
    const isValidZip = zipPattern?.test(this.state.zip);

    if (!isValidZip) {
      this.setState({
        submitButtonDisabled: false,
      });
      return message.error(
        formatMessage({
          id: 'app.visitor.shippingAddress.zip.invalid',
          description: 'Error message for invalid zip code',
          defaultMessage:
            "Please enter valid zip/postal code. Otherwise we'll not be able to process your return.",
        }),
        5
      );
    }

    const formValues = {
      shippingCountryCode: countryCode?.toUpperCase(),
      shippingProvinceCode: province,
      shippingFirstName: first_name,
      shippingLastName: last_name,
      shippingAddress1: address1,
      shippingAddress2: address2,
      shippingCity: city,
      shippingZip: zip,
      shippingCountry: country,
    };

    const response = await axios.post(
      '/api/visitors/return/updateShippingAddress',
      {
        companyIdentifier,
        returnNumber,
        formValues,
      }
    );

    if (response.data.status === 'error') {
      return message.error(
        formatMessage({
          id: 'app.visitor.shippingAddress.save.error',
          description: 'Error message when saving shipping address fails',
          defaultMessage:
            'There was an error saving the shipping address for your return. Please try again.',
        }),
        5
      );
    }

    this.setState({
      submitButtonDisabled: false,
    });

    return this.props.history.push('/return/method');
  }

  checkField = (field, errorMessage) => {
    if (field == null || field === '') {
      this.setState({ submitButtonDisabled: false });
      return message.error(errorMessage, 5);
    }
    return false;
  };

  updateAddress = (e, field) => {
    this.setState({
      [field]: e.target.value,
    });
  };

  render() {
    const { formatMessage } = this.props.intl;
    return (
      <React.Fragment>
        <Card
          className="textCenter"
          title={
            <FormattedMessage
              id="app.visitor.shippingAddressInput.headline"
              description="Title of the optional step of the customer portal process ('Shipping Address')"
              defaultMessage="Confirm your shipping address"
              values={{}}
            />
          }
        >
          <Row justify="center" align="middle">
            <Col xs={24} md={10}>
              <h4 style={{ textAlign: 'center' }}>
                <FormattedMessage
                  id="app.visitor.shippingAddress.headline"
                  description="Headline of the optional step of the customer portal process ('Shipping Address')"
                  defaultMessage="Your original order shipping address is provided below"
                />
              </h4>
              <span style={{ textAlign: 'center' }}>
                <FormattedMessage
                  id="app.visitor.shippingAddress.description"
                  description="Headline of the optional step of the customer portal process ('Shipping Address')"
                  defaultMessage="You can update your shipping address for the exchange process. If you need the items to be sent to a different address (for example, if you're traveling or have moved), please enter the new shipping details below"
                />
              </span>
            </Col>
          </Row>

          <Row justify="center" align="middle" style={{ marginTop: 20 }}>
            <Col xs={24} md={10}>
              <Input
                value={this.state.first_name}
                onChange={(e) => this.updateAddress(e, 'first_name')}
                addonBefore={
                  <FormattedMessage
                    id="app.visitor.newReturn.giftReturnFirstName"
                    description="Label for customer name input"
                    defaultMessage="Your first name"
                  />
                }
                placeholder={formatMessage({
                  id: 'app.visitor.newReturn.giftReturnFirstNamePlaceholder',
                  description: 'Label for customer name input',
                  defaultMessage: 'Your first name',
                })}
              />
            </Col>
          </Row>

          <Row justify="center" align="middle" style={{ marginTop: 20 }}>
            <Col xs={24} md={10}>
              <Input
                value={this.state.last_name}
                onChange={(e) => this.updateAddress(e, 'last_name')}
                // addonBefore="Last Name: "
                // placeholder="Enter your last name"
                addonBefore={
                  <FormattedMessage
                    id="app.visitor.newReturn.giftReturnLastName"
                    description="Label for customer name input"
                    defaultMessage="Your last name"
                  />
                }
                placeholder={formatMessage({
                  id: 'app.visitor.newReturn.giftReturnLastNamePlaceholder',
                  description: 'Label for customer name input',
                  defaultMessage: 'Your last name',
                })}
              />
            </Col>
          </Row>

          <Row justify="center" align="middle" style={{ marginTop: 20 }}>
            <Col xs={24} md={10}>
              <Input
                value={this.state.address1}
                onChange={(e) => this.updateAddress(e, 'address1')}
                // addonBefore="Address Line 1: "
                // placeholder="Enter your address line 1"
                addonBefore={
                  <FormattedMessage
                    id="app.visitor.shippingAddress.addressLine1"
                    description="Label for customer address line 1 input"
                    defaultMessage="Your address line 1"
                  />
                }
                placeholder={formatMessage({
                  id: 'app.visitor.shippingAddress.addressLinePlaceholder1',
                  description: 'Label for customer address line 1 input',
                  defaultMessage: 'Your address line 1',
                })}
              />
            </Col>
          </Row>

          <Row justify="center" align="middle" style={{ marginTop: 20 }}>
            <Col xs={24} md={10}>
              <Input
                value={this.state.address2}
                onChange={(e) => this.updateAddress(e, 'address2')}
                // addonBefore="Address Line 2: "
                // placeholder="Enter your address line 2 (Optional)"
                addonBefore={
                  <FormattedMessage
                    id="app.visitor.shippingAddress.addressLine2"
                    description="Label for customer address line 2 input"
                    defaultMessage="Your address line 2 (Optional)"
                  />
                }
                placeholder={formatMessage({
                  id: 'app.visitor.shippingAddress.addressLinePlaceholder2',
                  description: 'Label for customer address line 2 input',
                  defaultMessage: 'Your address line 2 (Optional)',
                })}
              />
            </Col>
          </Row>

          <Row justify="center" align="middle" style={{ marginTop: 20 }}>
            <Col xs={24} md={10}>
              <Input
                value={this.state.city}
                onChange={(e) => this.updateAddress(e, 'city')}
                // addonBefore="City: "
                // placeholder="Enter your city"
                addonBefore={
                  <FormattedMessage
                    id="app.visitor.newReturn.city"
                    description="Label for customer city input"
                    defaultMessage="City"
                  />
                }
                placeholder={formatMessage({
                  id: 'app.visitor.newReturn.cityPlaceholder',
                  description: 'Label for customer city input',
                  defaultMessage: 'Your city',
                })}
              />
            </Col>
          </Row>

          <Row justify="center" align="middle" style={{ marginTop: 20 }}>
            <Col xs={24} md={10}>
              <Input
                value={this.state.province}
                onChange={(e) => this.updateAddress(e, 'province')}
                // addonBefore="State/Province: "
                // placeholder="Enter your state/province"
                addonBefore={
                  <FormattedMessage
                    id="app.visitor.newReturn.state"
                    description="Label for customer state input"
                    defaultMessage="State/Province"
                  />
                }
                placeholder={formatMessage({
                  id: 'app.visitor.newReturn.statePlaceholder',
                  description: 'Label for customer state input',
                  defaultMessage: 'Your state/province',
                })}
              />
            </Col>
          </Row>

          <Row justify="center" align="middle" style={{ marginTop: 20 }}>
            <Col xs={24} md={10}>
              <Input
                value={this.state.zip}
                onChange={(e) => this.updateAddress(e, 'zip')}
                // addonBefore="Zip/Postal Code: "
                // placeholder="Enter your postal code"
                addonBefore={
                  <FormattedMessage
                    id="app.visitor.newReturn.zipCode"
                    description="Label for customer zip code input"
                    defaultMessage="Zip/Postal Code"
                  />
                }
                placeholder={formatMessage({
                  id: 'app.visitor.newReturn.zipCodePlaceholder',
                  description: 'Label for customer zip code input',
                  defaultMessage: 'Your zip/postal code',
                })}
              />
            </Col>
          </Row>

          <Row justify="center" align="middle" style={{ marginTop: 20 }}>
            <Col xs={24} md={10}>
              <Input
                value={this.state.country}
                onChange={(e) => this.updateAddress(e, 'country')}
                // addonBefore="Country: "
                // placeholder="Enter your country"
                addonBefore={
                  <FormattedMessage
                    id="app.visitor.newReturn.country"
                    description="Label for customer country input"
                    defaultMessage="Country"
                  />
                }
                placeholder={formatMessage({
                  id: 'app.visitor.newReturn.countryPlaceholder',
                  description: 'Label for customer country input',
                  defaultMessage: 'Your country',
                })}
              />
            </Col>
          </Row>

          <Divider />

          <Row justify="center" align="top">
            <Col>
              <Button
                onClick={this.handleSubmit.bind(this)}
                type="primary"
                disabled={this.state.submitButtonDisabled}
                style={{
                  backgroundColor: this.props.brandColorButton,
                  borderColor: this.props.brandColorButton,
                }}
              >
                <span
                  style={{
                    color: this.props.brandColorButtonText,
                    fontStyle: this.props.brandFontStyle,
                  }}
                >
                  <FormattedMessage
                    id="app.visitor.order.buttonSubmit"
                    description="Button to continue from step 'order' to 'resolution'"
                    defaultMessage="Continue"
                  />
                </span>
              </Button>
            </Col>
          </Row>
        </Card>
      </React.Fragment>
    );
  }
}

const zipCodeRegexByCountry = {
  AF: /^\d{4}$/,
  AX: /^\d{5}$/,
  AL: /^\d{4}$/,
  DZ: /^\d{5}$/,
  AS: /^96799$/,
  AD: /^AD\d{3}$/,
  AI: /^AI\-\d{4}$/,
  AR: /^[A-Z]\d{4}\s[A-Z]{3}$/,
  AM: /^\d{4}$/,
  AZ: /^AZ\s\d{4}$/,
  BD: /^\d{4}$/,
  BB: /^\d{5}$/,
  BY: /^\d{6}$/,
  BE: /^\d{4}$/,
  BR: /^\d{5}\-\d{3}$/,
  VG: /^VG\d{4}$/,
  BN: /^[A-Z]{2}\d{4}$/,
  BG: /^\d{4}$/,
  KH: /^\d{6}$/,
  CA: /^[A-Z]\d[A-Z]\s\d[A-Z]\d$/,
  CV: /^\d{4}-\d{3}$/,
  KY: /^KY\d\-\d{4}$/,
  CL: /^\d{7}$/,
  CN: /^\d{6}$/,
  CO: /^\d{6}$/,
  CD: /^\d{7}$/,
  CR: /^\d{5}$/,
  HR: /^\d{5}$/,
  CU: /^\d{5}$/,
  CY: /^\d{4}$/,
  CZ: /^\d{3}\s\d{2}$/,
  DK: /^\d{4}$/,
  DO: /^\d{5}$/,
  EC: /^\d{6}$/,
  EG: /^\d{5}$/,
  SV: /^\d{5}$/,
  EE: /^\d{5}$/,
  SZ: /^[A-Z]\d{3}$/,
  FI: /^\d{5}$/,
  FR: /^\d{5}$/,
  DE: /^\d{5}$/,
  GH: /^[A-Z][A-Z0-9]\-\d{5}\-\d{4}$/,
  GI: /^GX11 1AA$/,
  GR: /^\d{3}\s\d{2}$/,
  GL: /^\d{4}$/,
  GT: /^\d{5}$/,
  GG: /^GY\d{1,2}\s\d[A-Z]{2}$/,
  HT: /^\d{4}$/,
  HU: /^\d{4}$/,
  IS: /^\d{3}$/,
  IN: /^\d{6}$/,
  ID: /^\d{5}$/,
  IR: /^\d{10}$/,
  IQ: /^\d{5}$/,
  IE: /^[A-Z](\d{2}|6W)\s[A-Z]{4}$/,
  IM: /^IM\d{1,2}\s\d[A-Z]{2}$/,
  IL: /^\d{5}\s\d{2}$/,
  IT: /^\d{5}$/,
  JP: /^\d{3}\-\d{4}$/,
  JE: /^JE\d\s\d[A-Z]{2}$/,
  KZ: /^[A-Z]\d{2}[A-Z]\d[A-Z]\d$/,
  KE: /^\d{5}$/,
  KI: /^KI0\d{3}$/,
  KG: /^\d{6}$/,
  LA: /^\d{5}$/,
  LV: /^\d{4}$/,
  LB: /^\d{4}\s\d{4}$/,
  LS: /^\d{3}$/,
  LR: /^\d{4}$/,
  LY: /^\d{5}$/,
  LI: /^\d{4}$/,
  LT: /^\d{5}$/,
  LU: /^\d{4}$/,
  MG: /^\d{3}$/,
  MW: /^\d{6}$/,
  MY: /^\d{5}$/,
  MV: /^\d{5}$/,
  ML: /^\d{5}$/,
  MT: /^[A-Z]{3}\s\d{4}$/,
  MX: /^\d{5}$/,
  MD: /^\d{4}$/,
  MC: /^9800\d$/,
  MN: /^\d{5}$/,
  ME: /^\d{5}$/,
  MS: /^MSR\d{4}$/,
  MA: /^\d{5}$/,
  MZ: /^\d{4}$/,
  MM: /^\d{7}$/,
  NA: /^\d{5}$/,
  NP: /^\d{5}$/,
  NL: /^\d{4}\s[A-Z]{2}$/,
  NZ: /^\d{4}$/,
  NI: /^\d{5}$/,
  NE: /^\d{4}$/,
  NG: /^\d{6}$/,
  NF: /^28\d{2}$/,
  MP: /^969\d{2}$/,
  NO: /^\d{4}$/,
  OM: /^\d{3}$/,
  PK: /^\d{5}$/,
  PS: /^P\d{3}\s\d{4}$/,
  PG: /^\d{3}$/,
  PY: /^\d{6}$/,
  PE: /^\d{5}$/,
  PH: /^\d{4}$/,
  PL: /^\d{2}\-\d{3}$/,
  PT: /^\d{4}\-\d{3}$/,
  PR: /^\d{5}$/,
  RO: /^\d{6}$/,
  RU: /^\d{6}$/,
  LC: /^LC\d{2}\s\d{3}$/,
  VC: /^VC\d{4}$/,
  WS: /^WS\d{4}$/,
  SM: /^\d{5}$/,
  SA: /^\d{5}$/,
  SN: /^\d{5}$/,
  RS: /^\d{5}$/,
  SG: /^\d{6}$/,
  SK: /^\d{3}\s\d{2}$/,
  SI: /^\d{4}$/,
  ZA: /^\d{4}$/,
  KR: /^\d{5}$/,
  ES: /^\d{5}$/,
  LK: /^\d{5}$/,
  SE: /^\d{3}\s\d{2}$/,
  CH: /^\d{4}$/,
  TW: /^\d{6}$/,
  TJ: /^\d{6}$/,
  TZ: /^\d{5}$/,
  TH: /^\d{5}$/,
  TT: /^\d{6}$/,
  TN: /^\d{4}$/,
  TR: /^\d{5}$/,
  TM: /^\d{6}$/,
  UG: /^\d{5}$/,
  UA: /^\d{5}$/,
  GB: /^[A-Z]{1,2}[0-9][A-Z0-9]?\s[0-9][A-Z]{2}$/,
  US: /^\d{5}$/,
  UY: /^\d{5}$/,
  UZ: /^\d{6}$/,
  VA: /^120$/,
  VE: /^\d{4}$/,
  VN: /^\d{5}$/,
  WF: /^\d{5}$/,
};

const countryNameToCode = {
  Afghanistan: 'AF',
  ÅlandIslands: 'AX',
  Albania: 'AL',
  Algeria: 'DZ',
  AmericanSamoa: 'AS',
  Andorra: 'AD',
  Anguilla: 'AI',
  Argentina: 'AR',
  Armenia: 'AM',
  Azerbaijan: 'AZ',
  Bangladesh: 'BD',
  Barbados: 'BB',
  Belarus: 'BY',
  Belgium: 'BE',
  Brazil: 'BR',
  BritishVirginIslands: 'VG',
  Brunei: 'BN',
  Bulgaria: 'BG',
  Cambodia: 'KH',
  Canada: 'CA',
  CapeVerde: 'CV',
  CaymanIslands: 'KY',
  Chile: 'CL',
  China: 'CN',
  Colombia: 'CO',
  CongoDemocraticRepublic: 'CD',
  CostaRica: 'CR',
  Croatia: 'HR',
  Cuba: 'CU',
  Cyprus: 'CY',
  CzechRepublic: 'CZ',
  Denmark: 'DK',
  DominicanRepublic: 'DO',
  Ecuador: 'EC',
  Egypt: 'EG',
  ElSalvador: 'SV',
  Estonia: 'EE',
  Eswatini: 'SZ',
  Finland: 'FI',
  France: 'FR',
  Germany: 'DE',
  Ghana: 'GH',
  Gibraltar: 'GI',
  Greece: 'GR',
  Greenland: 'GL',
  Guatemala: 'GT',
  Guernsey: 'GG',
  Haiti: 'HT',
  Hungary: 'HU',
  Iceland: 'IS',
  India: 'IN',
  Indonesia: 'ID',
  Iran: 'IR',
  Iraq: 'IQ',
  Ireland: 'IE',
  IsleofMan: 'IM',
  Israel: 'IL',
  Italy: 'IT',
  Japan: 'JP',
  Jersey: 'JE',
  Kazakhstan: 'KZ',
  Kenya: 'KE',
  Kiribati: 'KI',
  Kyrgyzstan: 'KG',
  Laos: 'LA',
  Latvia: 'LV',
  Lebanon: 'LB',
  Lesotho: 'LS',
  Liberia: 'LR',
  Libya: 'LY',
  Liechtenstein: 'LI',
  Lithuania: 'LT',
  Luxembourg: 'LU',
  Madagascar: 'MG',
  Malawi: 'MW',
  Malaysia: 'MY',
  Maldives: 'MV',
  Mali: 'ML',
  Malta: 'MT',
  Mexico: 'MX',
  Moldova: 'MD',
  Monaco: 'MC',
  Mongolia: 'MN',
  Montenegro: 'ME',
  Montserrat: 'MS',
  Morocco: 'MA',
  Mozambique: 'MZ',
  Myanmar: 'MM',
  Namibia: 'NA',
  Nepal: 'NP',
  Netherlands: 'NL',
  NewZealand: 'NZ',
  Nicaragua: 'NI',
  Niger: 'NE',
  Nigeria: 'NG',
  NorfolkIsland: 'NF',
  NorthernMarianaIslands: 'MP',
  Norway: 'NO',
  Oman: 'OM',
  Pakistan: 'PK',
  Palestine: 'PS',
  PapuaNewGuinea: 'PG',
  Paraguay: 'PY',
  Peru: 'PE',
  Philippines: 'PH',
  Poland: 'PL',
  Portugal: 'PT',
  PuertoRico: 'PR',
  Romania: 'RO',
  Russia: 'RU',
  SaintLucia: 'LC',
  SaintVincentAndTheGrenadines: 'VC',
  Samoa: 'WS',
  SanMarino: 'SM',
  SaudiArabia: 'SA',
  Senegal: 'SN',
  Serbia: 'RS',
  Singapore: 'SG',
  Slovakia: 'SK',
  Slovenia: 'SI',
  SouthAfrica: 'ZA',
  SouthKorea: 'KR',
  Spain: 'ES',
  SriLanka: 'LK',
  Sweden: 'SE',
  Switzerland: 'CH',
  Taiwan: 'TW',
  Tajikistan: 'TJ',
  Tanzania: 'TZ',
  Thailand: 'TH',
  TrinidadAndTobago: 'TT',
  Tunisia: 'TN',
  Turkey: 'TR',
  Turkmenistan: 'TM',
  Uganda: 'UG',
  Ukraine: 'UA',
  UnitedKingdom: 'GB',
  UnitedStates: 'US',
  Uruguay: 'UY',
  Uzbekistan: 'UZ',
  VaticanCity: 'VA',
  Venezuela: 'VE',
  Vietnam: 'VN',
  WallisAndFutuna: 'WF',
};

export default injectIntl(ExchangeShippingAddress);
